'use client';
import React, { useEffect, useRef, useState } from 'react';

interface AdCard {
  id: number;
  title: string;
  subtitle: string;
  tag: string;
  accent: string;
  bg: string;
  icon: string;
}

const adCards: AdCard[] = [
  {
    id: 1,
    title: 'Hotel Hasdrubal Hammamet',
    subtitle: '4 premium padel courts · Beachfront',
    tag: 'Featured',
    accent: 'from-[#0ea5e9] to-[#0369a1]',
    bg: 'bg-[#0c1a2e]',
    icon: '🏨',
  },
  {
    id: 2,
    title: 'Padel Club Sousse',
    subtitle: 'Night sessions available · Pro coaching',
    tag: 'New',
    accent: 'from-[#f59e0b] to-[#d97706]',
    bg: 'bg-[#1a1200]',
    icon: '🌙',
  },
  {
    id: 3,
    title: 'Port El Kantaoui Resort',
    subtitle: '6 courts · Tournament venue',
    tag: 'Top Rated',
    accent: 'from-[#10b981] to-[#059669]',
    bg: 'bg-[#001a10]',
    icon: '🏆',
  },
  {
    id: 4,
    title: 'Djerba Padel Paradise',
    subtitle: 'Island experience · 3 courts',
    tag: 'Exclusive',
    accent: 'from-[#a855f7] to-[#7c3aed]',
    bg: 'bg-[#0f0020]',
    icon: '🌴',
  },
  {
    id: 5,
    title: 'Tunis City Padel',
    subtitle: 'Urban courts · Open 7 days',
    tag: 'Popular',
    accent: 'from-[#ef4444] to-[#dc2626]',
    bg: 'bg-[#1a0000]',
    icon: '🏙️',
  },
  {
    id: 6,
    title: 'Monastir Beach Club',
    subtitle: 'Sea views · 2 courts · Café',
    tag: 'Scenic',
    accent: 'from-[#06b6d4] to-[#0891b2]',
    bg: 'bg-[#001520]',
    icon: '🌊',
  },
  {
    id: 7,
    title: 'Nabeul Padel Academy',
    subtitle: 'Training programs · All levels',
    tag: 'Academy',
    accent: 'from-[#f97316] to-[#ea580c]',
    bg: 'bg-[#1a0800]',
    icon: '🎓',
  },
  {
    id: 8,
    title: 'Gammarth Luxury Courts',
    subtitle: 'VIP experience · 5-star amenities',
    tag: 'Luxury',
    accent: 'from-[#eab308] to-[#ca8a04]',
    bg: 'bg-[#120f00]',
    icon: '⭐',
  },
  {
    id: 9,
    title: 'Sfax Padel Center',
    subtitle: 'Indoor & outdoor · 4 courts',
    tag: 'Indoor',
    accent: 'from-[#84cc16] to-[#65a30d]',
    bg: 'bg-[#071200]',
    icon: '🏟️',
  },
  {
    id: 10,
    title: 'Bizerte Coastal Padel',
    subtitle: 'Northern gem · Weekend packages',
    tag: 'Weekend',
    accent: 'from-[#ec4899] to-[#db2777]',
    bg: 'bg-[#1a0010]',
    icon: '🎯',
  },
];

export default function AdvertisingBanner() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [isPaused, setIsPaused] = useState(false);
  const positionRef = useRef(0);
  const animFrameRef = useRef<number | null>(null);
  const speed = 0.6;

  // Duplicate cards for seamless loop
  const loopCards = [...adCards, ...adCards];

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const cardWidth = 280 + 16; // card width + gap
    const totalWidth = adCards.length * cardWidth;

    const animate = () => {
      if (!isPaused) {
        positionRef.current += speed;
        if (positionRef.current >= totalWidth) {
          positionRef.current = 0;
        }
        if (track) {
          track.style.transform = `translateX(-${positionRef.current}px)`;
        }
      }
      animFrameRef.current = requestAnimationFrame(animate);
    };

    animFrameRef.current = requestAnimationFrame(animate);
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [isPaused]);

  return (
    <section className="w-full py-12 bg-gradient-to-br from-[#0a0f1e] via-[#0d1526] to-[#0a0f1e] overflow-hidden relative">
      {/* Ambient glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-32 bg-secondary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-32 bg-primary/10 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16 mb-8 relative z-10">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-secondary/80 mb-1">
              Sponsored Courts
            </p>
            <h2 className="text-2xl font-bold text-white leading-tight">
              Discover Premium Padel Venues
            </h2>
          </div>
          <a
            href="/hotel-manager-dashboard"
            className="shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-secondary text-white text-sm font-semibold hover:bg-secondary/90 transition-all duration-200 shadow-lg shadow-secondary/30 hover:shadow-secondary/50 hover:-translate-y-0.5"
          >
            <span>Publish with Us Now</span>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </a>
        </div>
      </div>

      {/* Carousel track */}
      <div
        className="relative z-10"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {/* Left fade */}
        <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-[#0a0f1e] to-transparent z-20 pointer-events-none" />
        {/* Right fade */}
        <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-[#0a0f1e] to-transparent z-20 pointer-events-none" />

        <div className="overflow-hidden">
          <div
            ref={trackRef}
            className="flex gap-4 will-change-transform"
            style={{ width: 'max-content' }}
          >
            {loopCards.map((card, idx) => (
              <div
                key={`ad-${card.id}-${idx}`}
                className={`relative flex-shrink-0 w-[280px] rounded-2xl overflow-hidden cursor-pointer group ${card.bg} border border-white/5 hover:border-white/15 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl`}
                style={{ height: '160px' }}
              >
                {/* Gradient accent bar */}
                <div className={`absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r ${card.accent}`} />

                {/* Tag */}
                <div className="absolute top-3 right-3">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-gradient-to-r ${card.accent} text-white`}>
                    {card.tag}
                  </span>
                </div>

                {/* Content */}
                <div className="p-5 h-full flex flex-col justify-between">
                  <div>
                    <div className="text-2xl mb-2">{card.icon}</div>
                    <h3 className="text-white font-bold text-sm leading-tight mb-1 pr-16">
                      {card.title}
                    </h3>
                    <p className="text-white/50 text-xs leading-relaxed">
                      {card.subtitle}
                    </p>
                  </div>

                  {/* CTA */}
                  <div className="flex items-center justify-between mt-3">
                    <span className={`text-xs font-semibold bg-gradient-to-r ${card.accent} bg-clip-text text-transparent group-hover:opacity-80 transition-opacity`}>
                      Book Now →
                    </span>
                    <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${card.accent} flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 translate-x-2 group-hover:translate-x-0`}>
                      <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom CTA strip */}
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16 mt-8 relative z-10">
        <div className="flex items-center justify-center gap-3 text-center">
          <span className="text-white/40 text-xs">Want your venue here?</span>
          <a
            href="/hotel-manager-dashboard"
            className="text-xs font-semibold text-secondary hover:text-secondary/80 underline underline-offset-2 transition-colors"
          >
            Publish with Us Now
          </a>
          <span className="text-white/20 text-xs">·</span>
          <span className="text-white/40 text-xs">Reach thousands of padel players across Tunisia</span>
        </div>
      </div>
    </section>
  );
}
